import React, { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ImagePlus, Loader2, X } from "lucide-react";
import { api, fileUrl, apiError } from "@/lib/api";
import { toast } from "sonner";

export function NoteComposer({ open, onOpenChange, onCreated }) {
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const reset = () => {
    setRecipient(""); setMessage(""); setAnonymous(false); setImage(null);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImage(data.path);
    } catch (err) {
      toast.error("Could not upload image");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (saving) return;
    if (!recipient.trim() || !message.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/notes", {
        recipient_name: recipient.trim(),
        message: message.trim(),
        is_anonymous: anonymous,
        image,
      });
      toast.success("Your note is planted in the Garden 🌸");
      reset();
      onOpenChange(false);
      onCreated && onCreated(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#150A1A] border border-pink-500/25 text-pink-50 sm:max-w-lg" data-testid="note-composer-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">Leave a love note</DialogTitle>
          <DialogDescription className="text-slate-400">Write to any member — signed or anonymous.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-1" noValidate>
          <div className="space-y-1.5">
            <Label className="text-pink-200 text-sm">To (member's name)</Label>
            <Input
              data-testid="composer-recipient-input"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="Who is this for?"
              className="bg-black/40 border-pink-500/25 text-pink-50 placeholder:text-slate-500"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-pink-200 text-sm">Your message</Label>
            <Textarea
              data-testid="composer-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Pour your heart out…"
              className="bg-black/40 border-pink-500/25 text-pink-50 placeholder:text-slate-500 resize-none"
            />
          </div>

          <div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} data-testid="composer-image-file" />
            {image ? (
              <div className="relative inline-block">
                <img src={fileUrl(image)} alt="attachment" className="h-24 rounded-xl border border-pink-500/25 object-cover" />
                <button
                  type="button"
                  onClick={() => setImage(null)}
                  className="absolute -top-2 -right-2 bg-rose-600 rounded-full p-1"
                  data-testid="composer-remove-image"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="composer-add-image-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 text-sm text-pink-300 hover:text-pink-200 border border-dashed border-pink-500/30 rounded-xl px-4 py-3 w-full justify-center transition-all hover:bg-white/5"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                {uploading ? "Uploading…" : "Add a picture (optional)"}
              </button>
            )}
          </div>

          <div className="flex items-center justify-between bg-black/30 rounded-xl px-4 py-3 border border-pink-500/15">
            <div>
              <p className="text-sm text-pink-100">Post anonymously</p>
              <p className="text-xs text-slate-400">Hide your name as "Anonymous Whispers"</p>
            </div>
            <Switch data-testid="composer-anonymous-switch" checked={anonymous} onCheckedChange={setAnonymous} />
          </div>

          <button
            data-testid="composer-submit-btn"
            type="button"
            onClick={submit}
            disabled={saving}
            className="w-full bg-gradient-to-r from-pink-600 to-rose-500 hover:from-pink-500 hover:to-rose-400 text-white font-medium py-3 rounded-full shadow-lg shadow-pink-500/25 transition-all hover:scale-[1.02] disabled:opacity-60"
          >
            {saving ? "Planting…" : "Plant this note 🌸"}
          </button>
          <p className="text-xs text-slate-400 text-center">Want roses behind it? Add them from the note after posting.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
